/**
 * BM-H452 Phase 1 — practice area client register discovery.
 *
 * Read only. This page starts and reads discovery runs over the configured
 * SharePoint roots and shows what was found, what is missing and what needs a
 * human decision. It deliberately has no export, no CSV, no Mailchimp control
 * and no contact list: extraction and export are later phases and must not be
 * reachable from here.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  AlertTriangle,
  CheckCircle,
  FolderSearch,
  Loader2,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";

const FILTERS = [
  { key: "missing_compliance_document", label: "Missing FORF or OB" },
  { key: "missing_completion_document", label: "Missing completion" },
  { key: "unreadable_document", label: "Unreadable document" },
  { key: "ambiguous_matter_reference", label: "Ambiguous reference" },
  { key: "duplicate_matter_reference", label: "Duplicate reference" },
];

const FLAG_LABELS = {
  missing_compliance_folder: "No compliance folder",
  missing_compliance_document: "No FORF or OB form",
  missing_completion_document: "No completion document",
  unreadable_document: "Unreadable document",
  ambiguous_document: "Ambiguous document",
  duplicate_matter_reference: "Duplicate reference",
  ambiguous_matter_reference: "Ambiguous reference",
  practice_area_code_mismatch: "Wrong practice area code",
  exclusion_word_in_matter_name: "Exclusion word in name",
};

const STATUS_STYLES = {
  completed: "bg-teal-dim/30 text-teal border-teal/50",
  running: "bg-blue-900/30 text-blue-400 border-blue-700/50",
  failed: "bg-status-red-dim/30 text-status-red border-red-700/50",
  no_population_examined:
    "bg-status-amber-dim/30 text-status-amber border-amber-700/50",
};

function getBonsaiUserId() {
  return (
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    ""
  );
}

function getUserEmail() {
  return (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    ""
  );
}

function getHeaders() {
  const userId = getBonsaiUserId();
  return {
    "X-Bonsai-User-Id": userId,
    "X-User-Email": getUserEmail(),
  };
}

function formatTimestamp(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }) {
  const style =
    STATUS_STYLES[status] || "bg-slate-800 text-slate-400 border-slate-600";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${style}`}
      data-testid="run-status"
    >
      {status === "no_population_examined" ? "Nothing examined" : status}
    </span>
  );
}

function CountCard({ label, value, tone = "slate" }) {
  const tones = {
    slate: "from-slate-800/60 to-slate-800/20 border-slate-600/30",
    amber: "from-amber-900/40 to-amber-900/10 border-amber-700/30",
    red: "from-red-900/40 to-red-900/10 border-red-700/30",
    teal: "from-teal-dim/40 to-teal-dim/10 border-teal/30",
  };
  return (
    <div
      className={`bg-gradient-to-br ${tones[tone]} border rounded-md p-4`}
      data-testid={`count-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <p className="text-xs text-slate-400 font-medium">{label}</p>
      <p className="text-2xl font-bold text-white mt-1">{value ?? 0}</p>
    </div>
  );
}

export default function ClientRegisterDiscovery() {
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [report, setReport] = useState(null);
  const [areaFilter, setAreaFilter] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const describeError = useCallback((err, fallback) => {
    const status = err?.response?.status;
    if (status === 403) {
      setForbidden(true);
      return (
        err?.response?.data?.detail ||
        "You are not authorised for any practice area register."
      );
    }
    return err?.response?.data?.detail || err?.message || fallback;
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/client-register/config`, {
        headers: getHeaders(),
      });
      setConfig(resp.data);
      setError(null);
    } catch (err) {
      setError(describeError(err, "Could not read the register configuration."));
    }
  }, [describeError]);

  const loadRuns = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/client-register/runs`, {
        headers: getHeaders(),
        params: { limit: 25 },
      });
      setRuns(resp.data.runs || []);
      setError(null);
      return resp.data.runs || [];
    } catch (err) {
      setError(describeError(err, "Could not read the discovery run history."));
      return [];
    }
  }, [describeError]);

  const loadReport = useCallback(
    async (runId) => {
      if (!runId) return;
      const params = { limit: 500 };
      if (areaFilter) params.practice_area_key = areaFilter;
      Object.keys(activeFilters).forEach((key) => {
        if (activeFilters[key]) params[key] = true;
      });
      try {
        const resp = await axios.get(
          `${API}/admin/client-register/runs/${runId}`,
          { headers: getHeaders(), params }
        );
        setReport(resp.data);
        setError(null);
      } catch (err) {
        setReport(null);
        setError(describeError(err, "Could not read the discovery report."));
      }
    },
    [areaFilter, activeFilters, describeError]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadConfig();
      const history = await loadRuns();
      if (!cancelled && history.length > 0) {
        setSelectedRunId(history[0].id);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRunId) loadReport(selectedRunId);
  }, [selectedRunId, loadReport]);

  const startRun = async () => {
    setStarting(true);
    setError(null);
    try {
      const body = {};
      if (areaFilter) body.practice_area_keys = [areaFilter];
      const resp = await axios.post(
        `${API}/admin/client-register/runs`,
        body,
        { headers: getHeaders() }
      );
      await loadRuns();
      setSelectedRunId(resp.data.run?.id || null);
    } catch (err) {
      setError(describeError(err, "The discovery run could not be started."));
    } finally {
      setStarting(false);
    }
  };

  const toggleFilter = (key) => {
    setActiveFilters((current) => ({ ...current, [key]: !current[key] }));
  };

  const counts = report?.run?.counts || {};
  const nothingExamined =
    report?.run?.status === "no_population_examined" ||
    (report?.run && (counts.candidate_matters || 0) === 0);

  if (loading) {
    return (
      <div className="p-6 text-slate-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading client register discovery
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="p-6" data-testid="forbidden-panel">
        <div className="flex items-center gap-2 text-status-red">
          <ShieldAlert className="w-5 h-5" />
          <h1 className="text-lg font-semibold">Not authorised</h1>
        </div>
        <p className="text-sm text-slate-400 mt-2 max-w-xl">
          {error ||
            "You are not authorised for any practice area register. Registers are segregated by practice area and access is granted per person."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="client-register-discovery">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <FolderSearch className="w-5 h-5 text-slate-400" />
            Client register discovery
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Phase 1 is read only. It enumerates the configured SharePoint roots
            and reports which matters hold a FORF or an onboarding form, which
            hold a completion or closure document, and which need a human
            decision. Nothing is written to SharePoint, no client details are
            extracted and there is no export from this page.
          </p>
          {config?.rules_version && (
            <p className="text-xs text-slate-500 mt-2">
              Rules version {config.rules_version}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => loadRuns()}
            variant="outline"
            data-testid="refresh-runs"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={startRun}
            disabled={starting}
            data-testid="start-discovery-run"
          >
            {starting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FolderSearch className="w-4 h-4 mr-2" />
            )}
            {starting ? "Reading SharePoint" : "Start discovery run"}
          </Button>
        </div>
      </header>

      {error && (
        <div
          className="border border-red-700/50 bg-status-red-dim/20 text-status-red rounded-md p-3 text-sm flex items-start gap-2"
          data-testid="error-banner"
        >
          <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <section className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-400" htmlFor="area-filter">
          Practice area
        </label>
        <select
          id="area-filter"
          value={areaFilter}
          onChange={(event) => setAreaFilter(event.target.value)}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white"
          data-testid="practice-area-filter"
        >
          <option value="">All areas I am authorised for</option>
          {(config?.practice_areas || []).map((area) => (
            <option key={area.practice_area_key} value={area.practice_area_key}>
              {area.display_name}
            </option>
          ))}
        </select>
        {FILTERS.map((filter) => (
          <button
            key={filter.key}
            type="button"
            onClick={() => toggleFilter(filter.key)}
            data-testid={`filter-${filter.key}`}
            className={`px-2 py-1 rounded text-xs border ${
              activeFilters[filter.key]
                ? "bg-teal-dim/30 text-teal border-teal/50"
                : "bg-slate-900 text-slate-400 border-slate-700"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </section>

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <CountCard label="Matters examined" value={counts.candidate_matters} />
        <CountCard label="With FORF" value={counts.matters_with_forf} tone="teal" />
        <CountCard label="With OB form" value={counts.matters_with_ob} tone="teal" />
        <CountCard
          label="Missing both"
          value={counts.matters_missing_compliance_document}
          tone="amber"
        />
        <CountCard
          label="Missing completion"
          value={counts.matters_missing_completion_document}
          tone="amber"
        />
        <CountCard
          label="Needs a decision"
          value={
            (counts.matters_ambiguous || 0) +
            (counts.duplicate_matter_references || 0) +
            (counts.matters_with_unreadable_document || 0)
          }
          tone="red"
        />
      </section>

      {nothingExamined && report?.run && (
        <div
          className="border border-amber-700/50 bg-status-amber-dim/20 text-status-amber rounded-md p-3 text-sm flex items-start gap-2"
          data-testid="zero-population-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            This run examined no matter folders, so it proves nothing about the
            registers. It is not an all clear. Check the configured SharePoint
            roots and the app permissions before reading anything into it.
          </span>
        </div>
      )}

      {report?.run?.status_note && (
        <p className="text-xs text-slate-400" data-testid="status-note">
          {report.run.status_note}
        </p>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-2">
          <h2 className="text-sm font-semibold text-slate-300">Runs</h2>
          {runs.length === 0 && (
            <p className="text-xs text-slate-500" data-testid="no-runs">
              No discovery run has been recorded yet.
            </p>
          )}
          {runs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setSelectedRunId(entry.id)}
              data-testid={`run-${entry.id}`}
              className={`w-full text-left p-2 rounded border text-xs ${
                entry.id === selectedRunId
                  ? "border-teal/50 bg-teal-dim/20"
                  : "border-slate-700 bg-slate-900"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-300">
                  {formatTimestamp(entry.started_at)}
                </span>
                <StatusBadge status={entry.status} />
              </div>
              <p className="text-slate-500 mt-1">
                {(entry.practice_area_scope || []).join(", ") || "no areas"}
              </p>
              <p className="text-slate-500">
                {entry.counts?.candidate_matters ?? 0} matters,{" "}
                {entry.counts?.pages_read ?? 0} Graph pages
              </p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 space-y-3">
          <h2 className="text-sm font-semibold text-slate-300">
            Matters in this run
          </h2>
          {report?.items?.length === 0 && (
            <p className="text-xs text-slate-500" data-testid="no-items">
              No matter folder in this run matches the current filters.
            </p>
          )}
          <div className="space-y-2">
            {(report?.items || []).map((item) => (
              <div
                key={item.id}
                className="border border-slate-700 bg-slate-900 rounded p-3"
                data-testid="discovery-item"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm text-white font-medium">
                      {item.matter_reference || item.source_folder_name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {item.source_folder_path}
                    </p>
                  </div>
                  <span className="text-[11px] text-slate-400">
                    {item.practice_area_key}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(item.flags || []).length === 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-teal">
                      <CheckCircle className="w-3 h-3" />
                      Nothing outstanding
                    </span>
                  )}
                  {(item.flags || []).map((flag) => (
                    <span
                      key={flag}
                      className="px-1.5 py-0.5 rounded text-[11px] bg-slate-800 text-slate-300 border border-slate-600"
                    >
                      {FLAG_LABELS[flag] || flag}
                    </span>
                  ))}
                </div>
                {(item.documents_found || []).length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-2">
                    Found:{" "}
                    {(item.documents_found || [])
                      .map((doc) => `${doc.category.toUpperCase()} ${doc.name}`)
                      .join(", ")}
                  </p>
                )}
                {(item.documents_missing || []).length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Missing: {(item.documents_missing || []).join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="text-xs text-slate-500 border-t border-slate-800 pt-3">
        Phase 1 records folder and document names only. Client names, email
        addresses, marketing permissions and completion dates are not read in
        this phase and no marketing export exists yet.
      </footer>
    </div>
  );
}
